import React from 'react';
import { ChevronRight, ChevronDown } from 'react-feather';
import type { OutlinedDropdownProps } from './types';
import { useOutlinedDropdown } from './useOutlinedDropdown';
import { Checkbox } from '../Checkbox/Checkbox';

// ============================================================================
// OutlinedDropdown Component
// ============================================================================

const OutlinedDropdown: React.FC<OutlinedDropdownProps> = ({
  title,
  options,
  onChange,
  className = '',
  disabled = false,
  testId,
}) => {
  const {
    isOpen,
    internalOptions,
    isAllChecked,
    isIndeterminate,
    toggleOpen,
    toggleAll,
    toggleOption,
  } = useOutlinedDropdown({ options, onChange, disabled });

  return (
    <div
      className={`flex flex-col border border-neutral-80 rounded-lg overflow-hidden select-none bg-neutral-100 w-full ${disabled ? 'opacity-50 pointer-events-none' : ''} ${className}`}
      style={{
        fontFamily: 'Roboto, system-ui, Avenir, Helvetica, Arial, sans-serif',
        minWidth: 200,
      }}
      data-testid={testId ? `privilege-module-${testId}` : undefined}
    >
      {/* Header row */}
      <div
        className="flex items-center gap-1 px-1 py-2 pr-3 cursor-pointer"
        onClick={toggleOpen}
        role="button"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleOpen()}
        data-testid={testId ? `privilege-module-header-${testId}` : undefined}
      >
        <span
          onClick={(e) => {
            e.stopPropagation();
            if (!isOpen) {
              toggleOpen();
            }
          }}
        >
          <Checkbox
            checked={isAllChecked}
            indeterminate={isIndeterminate}
            onChange={toggleAll}
            disabled={disabled}
            {...(testId && { testId: `privilege-module-checkbox-${testId}` })}
          />
        </span>

        <span
          className={`flex-1 text-titleMedium text-neutral-10 min-w-0 ${!isOpen ? 'truncate' : 'break-words'}`}
        >
          {title}
        </span>

        <span className="flex items-center text-neutral-10">
          {isOpen ? (
            <ChevronDown size={24} color="#181A1B" />
          ) : (
            <ChevronRight size={24} color="#181A1B" />
          )}
        </span>
      </div>

      {/* Options list */}
      {isOpen && (
        <ul className="flex flex-col">
          {internalOptions.map((option, index) => (
            <li
              key={option.value}
              className={`flex items-center px-1 ${index === internalOptions.length - 1 ? 'pb-2' : ''} ${option.disabled ? 'cursor-not-allowed' : ''}`}
              role="option"
              aria-selected={option.checked ?? false}
              data-testid={`privilege-option-${option.value}`}
            >
              <Checkbox
                checked={option.checked ?? false}
                onChange={() => toggleOption(option.value)}
                label={option.label}
                disabled={disabled || !!option.disabled}
                labelClassName="text-bodyMedium font-normal"
                testId={`privilege-option-checkbox-${option.value}`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default OutlinedDropdown;
